<script setup lang="ts">
import GoogleButton from "@auth-frontend-vue/components/GoogleButton.vue";
import PasskeyButton from "@auth-frontend-vue/components/PasskeyButton.vue";
import { Card, CardContent } from "@/components/ui/card";
import EmailStepForm from '@auth-frontend-vue/components/forms/auth-forms/EmailStepForm.vue';
import PhoneSignupForm from '@auth-frontend-vue/components/forms/auth-forms/PhoneSignupForm.vue';
import PhoneLoginForm from '@auth-frontend-vue/components/forms/auth-forms/PhoneLoginForm.vue';
import PhoneOtpVerifyForm from '@auth-frontend-vue/components/forms/auth-forms/PhoneOtpVerifyForm.vue';
import LoginForm from '@auth-frontend-vue/components/forms/auth-forms/LoginForm.vue';
import SignupForm from '@auth-frontend-vue/components/forms/auth-forms/signup/SignupForm.vue';
import OTPVerifyForm from '@auth-frontend-vue/components/forms/auth-forms/otp/OTPVerifyForm.vue';
import {
    authEmail,
    authPhoneNumber,
    authStep,
    goToEmailStep,
    otpReturnStep,
    userHasPassword,
} from '@auth-frontend-vue/stores/authFlow.store';
import { sendOTP, sendPhoneOTP } from '@auth-frontend-vue/lib/auth.actions';
import { showTurnstile } from "@/ui-plus/dialog/showTurnstile";
import { toast } from "@/ui-plus/sonner";
import { ref } from "vue";

/** autofilled on email step; passed into LoginForm */
const loginPassword = ref('');

function onEmailChecked(result: {
    email: string;
    exists: boolean;
    hasPassword: boolean;
    password: string;
}) {
    authEmail.value = result.email;
    userHasPassword.value = result.hasPassword;
    loginPassword.value = result.password;
    if (!result.exists) {
        authStep.value = 'signup';
        return;
    }
    if (result.hasPassword) {
        authStep.value = 'login';
        return;
    }
    // No password — OTP only; cancel returns to identifier step
    startEmailOtpFlow('email');
}

async function onPhoneChecked(result: {
    phoneNumber: string;
    exists: boolean;
    hasPassword: boolean;
    password: string;
}) {
    authPhoneNumber.value = result.phoneNumber;
    userHasPassword.value = result.hasPassword;
    loginPassword.value = result.password;
    if (!result.exists) {
        authStep.value = 'phoneSignup';
        return;
    }
    if (result.hasPassword) {
        authStep.value = 'phoneLogin';
        return;
    }
    // No password — OTP only; cancel returns to identifier step
    await startPhoneOtpFlow('email');
}

function onChangeIdentifier() {
    loginPassword.value = '';
    goToEmailStep();
}

async function startEmailOtpFlow(returnStep: typeof authStep.value) {
    otpReturnStep.value = returnStep;
    const { error } = await sendOTP({
        verifyEmail: authEmail.value,
        turnstile: await showTurnstile(import.meta.env.PUBLIC_TURNSTILE_SITE_KEY)
    });
    if (error) {
        toast.error(error.message ?? 'An error occurred');
        return;
    }
    authStep.value = 'otp';
}

async function startPhoneOtpFlow(returnStep: typeof authStep.value) {
    otpReturnStep.value = returnStep;
    const { error } = await sendPhoneOTP({
        phoneNumber: authPhoneNumber.value,
        turnstile: await showTurnstile(import.meta.env.PUBLIC_TURNSTILE_SITE_KEY)
    });
    if (error) {
        toast.error(error.message ?? 'An error occurred');
        return;
    }
    authStep.value = 'phoneOtp';
}

function onOtpCancel() {
    authStep.value = otpReturnStep.value;
}

function onPhoneOtpCancel() {
    authStep.value = otpReturnStep.value;
}

function onForgotPassword() {
    startEmailOtpFlow('login');
}

function onForgotPhonePassword() {
    startPhoneOtpFlow('phoneLogin');
}

function onOtpRequired() {
    startEmailOtpFlow('login');
}

function onUseCodeInstead() {
    startEmailOtpFlow('signup');
}

function onUsePhoneCodeInstead() {
    startPhoneOtpFlow('phoneSignup');
}

const isPhoneFlowStep = (step: typeof authStep.value) =>
    step === 'phoneSignup' || step === 'phoneLogin' || step === 'phoneOtp';
</script>

<template>
    <div class="my-12 space-y-4">
        <GoogleButton />
        <PasskeyButton />
    </div>

    <Card>
        <CardContent class="my-6">


            <div v-if="authStep !== 'email'"
                 class="mb-6 flex items-center justify-between gap-2">
                <p class="text-sm font-medium truncate">
                    {{ isPhoneFlowStep(authStep) ? authPhoneNumber : authEmail }}
                </p>
                <button type="button"
                        class="text-sm underline shrink-0"
                        @click="onChangeIdentifier">
                    {{ $t('change') }}
                </button>
            </div>

            <EmailStepForm v-if="authStep === 'email'"
                           @checked="onEmailChecked"
                           @phone-checked="onPhoneChecked" />

            <LoginForm v-else-if="authStep === 'login'"
                       :email="authEmail"
                       :password="loginPassword"
                       @forgot-password="onForgotPassword"
                       @otp-required="onOtpRequired" />
            <SignupForm v-else-if="authStep === 'signup'"
                        :email="authEmail"
                        @use-code-instead="onUseCodeInstead" />
            <PhoneSignupForm v-else-if="authStep === 'phoneSignup'"
                             :phone-number="authPhoneNumber"
                             @use-code-instead="onUsePhoneCodeInstead" />
            <PhoneLoginForm v-else-if="authStep === 'phoneLogin'"
                            :phone-number="authPhoneNumber"
                            :password="loginPassword"
                            @forgot-password="onForgotPhonePassword" />

            <div v-else-if="authStep === 'otp'">
                <h1 class="text-2xl font-bold mb-1">{{ $t('enterCode') }}</h1>
                <p class="text-sm text-muted-foreground mb-6">{{ $t('codeSentToEmail') }}</p>
                <OTPVerifyForm :verify-email="authEmail"
                               @on-cancel="onOtpCancel" />
            </div>
            <div v-else-if="authStep === 'phoneOtp'">
                <h1 class="text-2xl font-bold mb-1">{{ $t('enterCode') }}</h1>
                <p class="text-sm text-muted-foreground mb-6">{{ $t('codeSentToPhone') }}</p>
                <PhoneOtpVerifyForm :phone-number="authPhoneNumber"
                                    @on-cancel="onPhoneOtpCancel" />
            </div>


        </CardContent>
    </Card>
</template>
